import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, ChevronLeft, ChevronRight } from 'lucide-react';

const LANGUAGES = [
  {
    name: 'Hindi',
    bg: '#FFFBEB', border: '#FDE68A', accent: '#D97706', dot: '#F59E0B',
    description: 'Pure desi. Feels like home.',
    detail: 'North India · UP · Bihar · MP · Rajasthan',
  },
  {
    name: 'Tamil',
    bg: '#F0FDF4', border: '#86EFAC', accent: '#16A34A', dot: '#22C55E',
    description: 'Real Chennai, not textbook Tamil.',
    detail: 'Tamil Nadu · Pan-India Tamil speakers',
  },
  {
    name: 'Telugu',
    bg: '#EFF6FF', border: '#93C5FD', accent: '#2563EB', dot: '#3B82F6',
    description: 'Warm Hyderabad, genuinely local.',
    detail: 'Andhra Pradesh · Telangana',
  },
  {
    name: 'Kannada',
    bg: '#FAF5FF', border: '#D8B4FE', accent: '#7C3AED', dot: '#8B5CF6',
    description: 'How Bengaluru actually speaks.',
    detail: 'Karnataka · Bengaluru · Tier-2 towns',
  },
  {
    name: 'Marathi',
    bg: '#FFF7ED', border: '#FED7AA', accent: '#EA580C', dot: '#F97316',
    description: 'Punchy, direct — Pune approved.',
    detail: 'Maharashtra · Mumbai · Pune · Nashik',
  },
  {
    name: 'Gujarati',
    bg: '#FEFCE8', border: '#FEF08A', accent: '#CA8A04', dot: '#EAB308',
    description: 'Feels like a trusted neighbour.',
    detail: 'Gujarat · Surat · Ahmedabad',
  },
  {
    name: 'Malayalam',
    bg: '#F0FDFA', border: '#99F6E4', accent: '#0D9488', dot: '#14B8A6',
    description: 'Flowing, familiar Kerala voice.',
    detail: 'Kerala · NRI Malayali buyers',
  },
  {
    name: 'Bengali',
    bg: '#FFF1F2', border: '#FECDD3', accent: '#E11D48', dot: '#F43F5E',
    description: 'Warm Kolkata street energy.',
    detail: 'West Bengal · Bengali diaspora',
  },
  {
    name: 'Punjabi',
    bg: '#FFF7ED', border: '#FED7AA', accent: '#C2410C', dot: '#EA580C',
    description: 'Full energy. Makes them smile.',
    detail: 'Punjab · Haryana · Delhi · Diaspora',
  },
  {
    name: 'English',
    bg: '#F8FAFC', border: '#CBD5E1', accent: '#475569', dot: '#64748B',
    description: 'Friendly, not corporate. Pan-India.',
    detail: 'Metros · Tier-1 buyers · Pan-India',
  },
];

const StackedCard = ({ active, activeIndex, onPrev, onNext }) => {
  const behind1 = LANGUAGES[(activeIndex + 1) % LANGUAGES.length];
  const behind2 = LANGUAGES[(activeIndex + 2) % LANGUAGES.length];

  return (
    <div className="flex items-center gap-2 sm:gap-4">
      {/* Prev arrow */}
      <button
        onClick={onPrev}
        className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-full border border-[#E7E5E4] bg-white hover:bg-[#F5F5F4] flex items-center justify-center transition-colors shadow-sm"
        aria-label="Previous language"
      >
        <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 text-[#57534E]" />
      </button>

      {/* Card stack */}
      <div className="relative flex-1" style={{ height: '190px' }}>
        {/* Card 3 — furthest behind */}
        <div
          className="absolute inset-x-0 rounded-2xl sm:rounded-3xl border-2"
          style={{
            top: 14, bottom: -14,
            background: behind2.bg,
            borderColor: behind2.border,
            transform: 'rotate(3deg) translateX(8px)',
            zIndex: 1,
          }}
        />
        {/* Card 2 — middle */}
        <div
          className="absolute inset-x-0 rounded-2xl sm:rounded-3xl border-2"
          style={{
            top: 8, bottom: -8,
            background: behind1.bg,
            borderColor: behind1.border,
            transform: 'rotate(-2deg) translateX(-5px)',
            zIndex: 2,
          }}
        />
        {/* Active card — front */}
        <AnimatePresence mode="wait">
          <motion.div
            key={active.name}
            initial={{ opacity: 0, rotate: -6, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, rotate: 0, y: 0, scale: 1 }}
            exit={{ opacity: 0, rotate: 8, y: -30, scale: 0.95 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="absolute inset-0 rounded-2xl sm:rounded-3xl border-2 p-4 sm:p-6 flex flex-col justify-between"
            style={{ background: active.bg, borderColor: active.border, zIndex: 3 }}
          >
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: active.dot }} />
                <span className="text-sm sm:text-base font-bold" style={{ color: active.accent }}>
                  {active.name}
                </span>
                <span className="ml-auto text-xs text-[#A8A29E]">
                  {activeIndex + 1} / {LANGUAGES.length}
                </span>
              </div>
              <p className="text-[#1C1917] text-base sm:text-lg font-bold leading-snug">
                "{active.description}"
              </p>
            </div>
            <p className="text-xs font-medium pt-2 border-t" style={{ color: active.accent, borderColor: active.border }}>
              {active.detail}
            </p>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Next arrow */}
      <button
        onClick={onNext}
        className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-full border border-[#E7E5E4] bg-white hover:bg-[#F5F5F4] flex items-center justify-center transition-colors shadow-sm"
        aria-label="Next language"
      >
        <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-[#57534E]" />
      </button>
    </div>
  );
};

const VoiceAISection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const active = LANGUAGES[activeIndex];

  const handlePrev = () => setActiveIndex((activeIndex - 1 + LANGUAGES.length) % LANGUAGES.length);
  const handleNext = () => setActiveIndex((activeIndex + 1) % LANGUAGES.length);

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="languages"
      data-testid="languages-section"
      className="py-24 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mb-16"
        >
          <span className="text-[#2563EB] font-semibold text-sm uppercase tracking-wider">
            Languages
          </span>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1C1917] tracking-tight">
            Not translated.{' '}
            <span className="text-[#2563EB]">Native.</span>
          </h2>
          <p className="mt-5 text-[#57534E] text-base md:text-lg leading-relaxed">
            Our voice agents don't speak textbook Hindi or formal Tamil. They speak the way
            your customers do — with the natural rhythm, the local warmth, and the real feel
            of someone from their own neighbourhood.
          </p>
        </motion.div>

        {/* Stacked card with arrows */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-lg mx-auto mb-4"
        >
          <StackedCard
            active={active}
            activeIndex={activeIndex}
            onPrev={handlePrev}
            onNext={handleNext}
          />
        </motion.div>

        {/* Contact hint */}
        <div className="max-w-lg mx-auto mb-16 text-center">
          <p className="text-xs text-[#A8A29E]">
            Don't see your language?{' '}
            <button
              onClick={scrollToContact}
              className="text-[#2563EB] hover:underline font-medium"
            >
              Let us know
            </button>
          </p>
        </div>

        {/* Bottom strip */}
        <div className="grid sm:grid-cols-2 gap-5 max-w-2xl mx-auto">

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="bg-[#2563EB] rounded-2xl p-6 flex items-center gap-5"
          >
            <div className="flex-shrink-0">
              <div className="text-5xl font-bold text-white leading-none font-['DM_Sans']">3×</div>
              <div className="text-white/70 text-xs mt-1 uppercase tracking-wide">lift</div>
            </div>
            <div>
              <p className="text-white font-semibold text-sm mb-1">Better conversions</p>
              <p className="text-white/70 text-xs leading-relaxed">
                A native-sounding voice converts up to 3× better — because familiar feels trustworthy.
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-[#EFF6FF] rounded-2xl p-6 flex items-center gap-5 border border-[#DBEAFE]"
          >
            <div className="w-10 h-10 bg-[#2563EB] rounded-xl flex items-center justify-center flex-shrink-0">
              <MapPin className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-[#1C1917] font-semibold text-sm mb-1">Switch languages on the fly</p>
              <p className="text-[#57534E] text-xs leading-relaxed">
                Auto-detects by PIN code and location — or override per customer. Zero configuration.
              </p>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default VoiceAISection;
